import { FileType } from "../../constants/misc/FileType";
import useExportToFile from "../../utils/useExportToFile";
import { oktaPost } from "../oktaRequestWrapper";
import { ApiRoutes } from "../apiRoutes";
import { useQuery } from "react-query";
import { InvoicesDashboardResponse } from "@/app/features/DebtorPortal/Dashboard/dashboard.types";
import { useCompany } from "../../utils/hooks/useCompany";

export interface Pagination {
  size?: number;
  cursor?: string;
  page?: number;
}

export interface FilterInfo {
  condition?: string;
  field?: string;
  fieldExpression?: string;
  filterType?: string;
  value?: string;
  filter?: FilterInfo[];
}

export interface SortInfo {
  field: string;
  order: 'asc' | 'desc';
}

interface QueryRequest {
  returnFullResultSet: boolean;
  pagination: Pagination;
  filter?: FilterInfo;
  sort?: SortInfo[];
  fields?: string[];
  fieldsForFilters?: string[];
  fieldsToTotal?: string[];
}

export interface SearchInvoicesRequest {
  pageSize?: number;
  page?: number;
  cursor?: string;
  filter?: FilterInfo;
  sort?: SortInfo[];
  fields?: string[];
  returnFullResultSet?: boolean;
  fieldsForFilters?: string[];
  isCsv: boolean;
  paymentsNotReceived: boolean;
  needsAttention: boolean;
}

export interface UseSearchInvoicesOptions {
  enabled?: boolean;
}

const { exportFile } = useExportToFile();

export const getSearchInvoicesCSV = async (
  filter?: FilterInfo,
  sort?: SortInfo[],
  paymentsNotReceived?: boolean,
  needsAttention?: boolean,
  fileType: FileType = FileType.Xlsx,
) => {
  const queryBody: QueryRequest = {
    returnFullResultSet: true,
    fieldsForFilters: [],
    pagination: {
      size: 10000,
    },
    filter: filter,
    sort: sort,
  };

  const requestConfig = {
    headers: {
      'api-version': '2.0',
    },
    params: {
      fileType: fileType.toString(),
      paymentsNotReceived: paymentsNotReceived ?? false,
      needsAttention: needsAttention ?? false,
    },
    responseType: 'blob' as const,
  };

  const path = ApiRoutes.SearchInvoices;

  const response = await oktaPost(path, queryBody, requestConfig);
  await exportFile('Invoice List', response.data);
  return response.data;
};

export const useSearchInvoices = (
  request: SearchInvoicesRequest,
  options: UseSearchInvoicesOptions = {},
) => {
  const { enabled = true } = options;
  const { selectedCompanyId, initialized: companyInitialized } = useCompany();

  return useQuery({
    queryKey: [
      'searchInvoices',
      request.pageSize,
      request.page,
      request.cursor,
      request.filter,
      request.sort,
      request.isCsv,
      request.paymentsNotReceived,
      request.needsAttention,
      selectedCompanyId, // Refetch when company changes
    ],
    enabled: enabled && !request.isCsv && companyInitialized && selectedCompanyId !== null,
    queryFn: async (): Promise<InvoicesDashboardResponse> => {
      const queryBody: QueryRequest = {
        returnFullResultSet: request.returnFullResultSet || false,
        fieldsForFilters: request.fieldsForFilters || [
          'carrier.clientId',
          'carrier.clientName',
          'statusDebtor',
          'age',
        ],
        pagination: {
          size: request.pageSize || 10,
          cursor: request.cursor,
          page: request.page,
        },
        filter: request.filter,
        sort: request.sort,
        fields: request.fields,
      };

      const requestConfig = {
        headers: {
          'api-version': '2.0',
        },
        params: {
          paymentsNotReceived: request.paymentsNotReceived,
          needsAttention: request.needsAttention,
        },
      };

      const route = ApiRoutes.SearchInvoices;

      try {
        const response = await oktaPost<InvoicesDashboardResponse>(
          route,
          queryBody,
          requestConfig,
        );
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 400) {
          return {
            results: [],
            availableFilters: {},
            pageSize: 25,
            totalResults: 0,
            currentPage: 0,
          };
        }
        throw error;
      }
    },
  });
};
